import { useCallback, useEffect, useMemo, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { useHostingAdvancedImages } from '@newfold/huapi-js';

import { useTenant } from '~/components/TenantProvider';
import useAccount from '~/hooks/useAccount';
import useFilteredHostingSites from '~/hooks/useFilteredHostingSites';
import useRefetchInterval from '~/hooks/useRefetchInterval';
import { PublicImageItem } from '~/Nova/scenes/HostingList/utils/types';
import {
  FAILED_STATUSES_WITH_SYNC_CHECK,
  PENDING_STATUSES,
} from '~/scenes/Hosting/scenes/HostingDetail/components/Sites/utils/constants';

import {
  fetchCurrentImage,
  findHostingPlanById,
  groupImagesByType,
  isSelfManagedDedicated,
} from '../../utils/helpers';
import { useReimagePolling } from './hooks/useReimagePolling';
import { useReimageSearch } from './hooks/useReimageSearch';
import { useReimageStatus } from './hooks/useReimageStatus';
import SelfManagedHostingLayout from './scenes/SelfManagedHostingLayout';
import Access from './scenes/SelfManagedHostingLayout/Access';
import AiTooling from './scenes/SelfManagedHostingLayout/AiTooling';
import Licenses from './scenes/SelfManagedHostingLayout/Licenses';
import Overview from './scenes/SelfManagedHostingLayout/Overview';
import ReImageServerApps from './scenes/SelfManagedHostingLayout/ReImageServerApps';
import ReImageServerOS from './scenes/SelfManagedHostingLayout/ReImageServerOS';
import Snapshots from './scenes/SelfManagedHostingLayout/Snapshots';
import SSHKeys from './scenes/SelfManagedHostingLayout/SSHKeys';
import { APP_IMAGE_TYPES, REIMAGE_ROUTE_TO_TAB_PATH } from './utils/constants';

const SelfManagedHosting: React.FC = () => {
  const { id: hostingId } = useAccount();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { refetchTime } = useRefetchInterval();
  const { hostingPackages } = useTenant();

  const [serverStatus, setServerStatus] = useState('');
  const [appActionInProgress, setAppActionInProgress] = useState(false);

  // Reimage status management
  const {
    serverStatus: fetchedServerStatus,
    isReimaging,
    reimageFailed,
    refetchServerStatus,
    setIsReimaging,
    setReimageFailed,
  } = useReimageStatus(hostingId);

  // Handle status changes from polling
  const handleStatusChange = useCallback(
    (newIsReimaging: boolean, newReimageFailed: boolean) => {
      setIsReimaging(newIsReimaging);
      setReimageFailed(newReimageFailed);
    },
    [setIsReimaging, setReimageFailed],
  );

  // Poll server status when reimaging
  useReimagePolling({
    hostingId,
    isReimaging,
    refetchServerStatus,
    onStatusChange: handleStatusChange,
  });

  const hostingPlan = useMemo(
    () => findHostingPlanById(hostingPackages, hostingId),
    [hostingPackages, hostingId],
  );

  const { account_info: { type = '' } = {}, billing } = hostingPlan || {};

  const isSelfManagedDedi = isSelfManagedDedicated(type, billing?.prod_name);

  const isCpanelInstalled = useMemo(
    () => hostingPlan?.account_info?.software?.includes('cPanel') || false,
    [hostingPlan],
  );

  const nonDockerAppName = hostingPlan?.account_info?.image?.app ?? '';
  const isLoadingHostingPlans = !hostingPackages;

  const {
    data: OSimagesList,
    isLoading: isLoadingOSImages,
    isError: isErrorOSImages,
  } = useHostingAdvancedImages(
    hostingId,
    {},
    { query: { enabled: !!hostingId } },
  );

  const osImages = OSimagesList?.data?.images;

  const osAdvancedImages = useMemo(
    () => (osImages ?? []) as PublicImageItem[],
    [osImages],
  );

  const allOsFilteredList = useMemo(
    () => groupImagesByType(osAdvancedImages),
    [osAdvancedImages],
  );

  const applicationImages = useMemo(
    () =>
      osAdvancedImages.filter(
        (img) => img.type && APP_IMAGE_TYPES.includes(img.type),
      ),
    [osAdvancedImages],
  );

  const currentRouteSegment = pathname.split('/').findLast(Boolean) || '';

  const currentReimageTab =
    currentRouteSegment in REIMAGE_ROUTE_TO_TAB_PATH
      ? (currentRouteSegment as keyof typeof REIMAGE_ROUTE_TO_TAB_PATH)
      : null;

  const {
    searchQuery,
    onSearchChange,
    filteredOsImages,
    filteredApplicationImages,
  } = useReimageSearch({
    pathname,
    navigate,
    groupedOsImages: allOsFilteredList,
    applicationImages,
  });

  const osCurrentImage = fetchCurrentImage(osAdvancedImages);

  const isOsImagesAvailable = !Object.keys(allOsFilteredList).length;

  const serverStatusValue = serverStatus || fetchedServerStatus || '';
  const isVps = type === 'vps';
  const { data: appsData, isLoading: isLoadingApp } = useFilteredHostingSites(
    hostingId,
    { limit: 1, page: 1, search: '' },
    {
      query: {
        enabled: !!hostingId,
        refetchInterval: appActionInProgress && refetchTime,
        refetchIntervalInBackground: appActionInProgress,
      },
    },
  );

  const installedAppData = useMemo(
    () => appsData?.data?.items?.find((item) => item.type === 'docker'),
    [appsData],
  );

  useEffect(() => {
    if (!installedAppData) {
      setAppActionInProgress(false);
      return;
    }
    const { status = '', jobs } = installedAppData;
    const inProgress = FAILED_STATUSES_WITH_SYNC_CHECK.includes(status)
      ? jobs && Object.values(jobs).includes('sync')
      : PENDING_STATUSES.includes(status);

    setAppActionInProgress(Boolean(inProgress));
  }, [installedAppData]);

  return (
    <Routes>
      <Route
        path="/"
        element={
          <SelfManagedHostingLayout
            isLoadingAccounts={isLoadingHostingPlans}
            hostingPlanDetails={hostingPlan}
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
            currentReimageTab={currentReimageTab}
          />
        }
      >
        <Route
          path="overview"
          element={
            <Overview
              hostingPlanDetails={hostingPlan}
              installedAppData={installedAppData}
              installedAppDataLoading={isLoadingApp}
              serverStatus={serverStatus}
              setServerStatus={setServerStatus}
              isReimaging={isReimaging}
              reimageFailed={reimageFailed}
              isOsImagesAvailable={isOsImagesAvailable}
              isErrorOSImages={isErrorOSImages}
              appActionInProgress={appActionInProgress}
              osAdvancedImages={osAdvancedImages}
            />
          }
        />
        <Route
          path="license"
          element={<Licenses osAdvancedImages={osAdvancedImages} />}
        />
        <Route path="snapshots" element={<Snapshots />} />
        <Route
          path="ssh-keys"
          element={<SSHKeys serverStatus={serverStatusValue} isVps={isVps} />}
        />
        <Route path="access" element={<Access />} />
        <Route
          path="ai-tooling"
          element={<AiTooling hostingPlanDetails={hostingPlan} />}
        />
        <Route path="reimage-server/*">
          <Route
            path="plain-os"
            element={
              <ReImageServerOS
                osFilteredList={filteredOsImages}
                hostingId={hostingId}
                osCurrentImage={osCurrentImage}
                isLoadingOSImages={isLoadingOSImages}
                isSelfManagedDedi={isSelfManagedDedi}
                searchQuery={searchQuery}
              />
            }
          />
          <Route
            path="applications"
            element={
              <ReImageServerApps
                images={filteredApplicationImages}
                installedAppData={installedAppData}
                hostingId={hostingId}
                isCpanelInstalled={isCpanelInstalled}
                osAdvancedImages={osAdvancedImages}
                nonDockerAppName={nonDockerAppName}
                isSelfManagedDedi={isSelfManagedDedi}
                searchQuery={searchQuery}
              />
            }
          />
        </Route>
      </Route>
    </Routes>
  );
};

export default SelfManagedHosting;
